<template>
  <div class="document-manager">
    <header class="manager-header">
      <div>
        <div class="eyebrow">Dokumentenablage</div>
        <h2>{{ areaLabel }} verwalten</h2>
        <p>{{ isLeadership ? "Stelle mehrere Dateien pro Eintrag bereit. Jede Datei behält ihren eigenen Versionsverlauf." : "Stelle aktuelle Dokumente bereit. Beim Ersetzen bleibt jede frühere Version nachvollziehbar und abrufbar." }}</p>
      </div>
      <v-btn color="#58d388" class="black--text create-action" depressed @click="openDialog()">
        <v-icon left>mdi-file-plus-outline</v-icon>Dokument hinzufügen
      </v-btn>
    </header>

    <div v-if="loading" class="document-grid">
      <v-skeleton-loader v-for="index in 4" :key="index" dark type="article, actions" class="document-card" />
    </div>
    <div v-else-if="documents.length" class="document-grid">
      <article v-for="document in documents" :key="document._id" class="document-card" :class="{ inactive: !document.active }">
        <div class="document-heading">
          <span><v-icon>{{ documentIcon(document.currentFile) }}</v-icon></span>
          <div>
            <small>{{ document.category || "Allgemein" }}</small>
            <h3>{{ document.title }}</h3>
          </div>
          <span class="status-badge" :class="{ active: document.active }">{{ document.active ? "Veröffentlicht" : "Entwurf" }}</span>
        </div>
        <p>{{ document.description || "Keine Beschreibung hinterlegt." }}</p>
        <div class="document-meta">
          <span v-if="isLeadership"><v-icon>mdi-paperclip</v-icon>{{ activeFiles(document).length }} Dateien</span>
          <span v-else><v-icon>mdi-history</v-icon>Version {{ document.version || 0 }}</span>
          <span><v-icon>mdi-file-multiple-outline</v-icon>{{ versionCount(document) }} Versionen</span>
        </div>
        <footer>
          <v-btn text color="#58d388" @click="openDialog(document)"><v-icon left>mdi-pencil-outline</v-icon>Bearbeiten</v-btn>
          <v-btn text color="#d4d4d8" :disabled="!versionCount(document)" @click="openVersions(document)">
            <v-icon left>mdi-history</v-icon>Versionen
          </v-btn>
          <v-btn icon color="#ff8d98" aria-label="Dokument löschen" @click="deleteDocument(document)"><v-icon>mdi-delete-outline</v-icon></v-btn>
        </footer>
      </article>
    </div>
    <div v-else class="empty-state">
      <v-icon>mdi-folder-plus-outline</v-icon>
      <h3>Noch keine Dokumente</h3>
      <p>Füge das erste Dokument für {{ areaLabel }} hinzu.</p>
    </div>

    <v-dialog v-model="dialog" max-width="44rem" persistent dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card class="dialog-card">
        <header class="dialog-header">
          <span><v-icon>{{ editingId ? "mdi-file-edit-outline" : "mdi-file-plus-outline" }}</v-icon></span>
          <div>
            <div class="eyebrow">{{ areaLabel }}</div>
            <h2>{{ editingId ? "Dokument bearbeiten" : "Dokument hinzufügen" }}</h2>
            <p>{{ isLeadership ? "Dateien hinzufügen, einzeln ersetzen oder entfernen. Änderungen werden beim Speichern übernommen." : editingId ? "Metadaten ändern oder eine neue Dateiversion hochladen." : "Erfasse das Dokument und lade die erste Version hoch." }}</p>
          </div>
          <v-btn icon aria-label="Dialog schliessen" :disabled="saving" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="dialog-body">
          <v-form ref="form" v-model="valid">
            <v-text-field :disabled="saving" v-model="form.title" label="Titel" outlined :rules="[(value) => !!value || 'Titel erforderlich']" />
            <v-combobox
              :disabled="saving"
              ref="categoryInput"
              v-model="form.category"
              :items="categoryOptions"
              :return-object="false"
              :loading="loading"
              label="Thema / Kategorie"
              hint="Vorhandene Kategorie auswählen oder eine neue eingeben."
              persistent-hint
              hide-no-data
              outlined />
            <v-textarea :disabled="saving" v-model="form.description" label="Kurzbeschreibung (optional)" outlined rows="3" />
            <section v-if="isLeadership" class="attachment-editor">
              <h3>Dateien</h3>
              <p>PDF oder Word · maximal 50 MB pro Datei. Entfernte Dateien bleiben im Versionsverlauf abrufbar.</p>
              <article v-for="entry in fileEntries" :key="entry.key" class="attachment-row" :class="{ removed: entry.removed }">
                <div class="attachment-heading">
                  <v-icon>{{ documentIcon(entry.file || entry.currentFile) }}</v-icon>
                  <div>
                    <strong>{{ entry.currentFile ? entry.currentFile.originalName : entry.file.name }}</strong>
                    <small>{{ entry.removed ? 'Wird entfernt' : entry._id ? `Version ${entry.version}` : 'Neue Datei' }}</small>
                  </div>
                  <v-btn icon :disabled="saving" :aria-label="entry.removed ? 'Entfernen rückgängig machen' : 'Datei entfernen'" @click="toggleFile(entry)">
                    <v-icon>{{ entry.removed ? 'mdi-undo' : 'mdi-close' }}</v-icon>
                  </v-btn>
                </div>
                <v-file-input
                  v-if="entry._id && !entry.removed"
                  :value="entry.file"
                  label="Diese Datei ersetzen (optional)"
                  :accept="fileAccept"
                  :disabled="saving"
                  :rules="[fileRule]"
                  show-size outlined hide-details="auto"
                  @change="setReplacement(entry, $event)" />
                <p v-if="entry.error" class="attachment-error" role="alert">{{ entry.error }}</p>
              </article>
              <v-file-input
                v-model="selectedFiles"
                label="Dateien hinzufügen"
                :accept="fileAccept"
                :disabled="saving"
                multiple outlined show-size
                hint="Du kannst mehrere Dateien gleichzeitig auswählen und weitere hinzufügen."
                persistent-hint
                @change="addFiles" />
            </section>
            <v-file-input
              v-else
              v-model="file"
              :label="editingId ? 'Neue Dateiversion (optional)' : 'Datei'"
              :accept="fileAccept"
              :hint="fileFormatsHint"
              persistent-hint
              :prepend-icon="documentIcon(file)"
              show-size
              outlined
              :disabled="saving"
              :rules="[fileRule]" />
            <div class="publish-control" :class="{ active: form.active }">
              <div>
                <strong>Dokument veröffentlichen</strong>
                <span>Nur veröffentlichte Dokumente sind für die Zielgruppe sichtbar.</span>
              </div>
              <v-switch :disabled="saving" v-model="form.active" color="#58d388" inset hide-details aria-label="Dokument veröffentlichen" />
            </div>
          </v-form>
          <div v-if="saving && uploadProgress > 0" class="upload-progress">
            <div><span>{{ uploadLabel || "Dokument wird hochgeladen" }}</span><strong>{{ uploadProgress }}%</strong></div>
            <v-progress-linear :value="uploadProgress" color="#009640" background-color="rgba(255,255,255,0.08)" height="8" rounded />
          </div>
          <v-alert v-if="dialogError" type="error" outlined text class="mt-4 mb-0">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn text :disabled="saving" @click="closeDialog">Abbrechen</v-btn>
          <v-spacer />
          <v-btn color="#58d388" class="black--text" depressed :disabled="!valid || saving || saveUncertain" :loading="saving" @click="saveDocument">
            <v-icon left>mdi-content-save-outline</v-icon>Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="versionsDialog" max-width="42rem" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="versionDocument" class="dialog-card">
        <header class="dialog-header">
          <span><v-icon>mdi-history</v-icon></span>
          <div>
            <div class="eyebrow">Versionsverlauf</div>
            <h2>{{ versionDocument.title }}</h2>
            <p>Alle bisher hochgeladenen Dateiversionen bleiben abrufbar.</p>
          </div>
          <v-btn icon aria-label="Dialog schliessen" @click="versionsDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="version-list">
          <section v-for="group in versionGroups" :key="group._id || 'document'" class="version-group">
            <h3 v-if="isLeadership">{{ group.currentFile && group.currentFile.originalName }}<span v-if="group.deletedAt"> · Entfernt</span></h3>
            <article v-for="version in group.versions" :key="version._id" class="version-row">
              <span><v-icon>{{ documentIcon(version.file) }}</v-icon></span>
              <div>
                <strong>Version {{ version.version }}<template v-if="version.version === group.version && !group.deletedAt"> · Aktuell</template></strong>
                <small>{{ formatDate(version.uploadedAt) }} · {{ version.file && version.file.originalName }}</small>
              </div>
              <v-btn icon aria-label="Version herunterladen" :loading="downloadingId === version._id" @click="downloadVersion(versionDocument, version, group)">
                <v-icon>mdi-download-outline</v-icon>
              </v-btn>
            </article>
          </section>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top right>{{ snackbar.text }}</v-snackbar>
  </div>
</template>

<script>
import api from "@/api"
import { uploadCloudflareMedia } from "@/services/cloudflareMediaUploadService"
import { getAcademyDocumentAccept, getAcademyDocumentFileName, getAcademyDocumentIcon, getAcademyDocumentType } from "@/utils/academyDocumentFiles"

const MAX_FILE_SIZE = 50 * 1024 * 1024

export default {
  name: "DocumentManager",
  props: {
    area: { type: String, default: "general" },
  },
  data() {
    return {
      documents: [],
      loading: false,
      dialog: false,
      versionsDialog: false,
      versionDocument: null,
      editingId: null,
      valid: false,
      form: this.emptyForm(),
      file: null,
      selectedFiles: [],
      fileEntries: [],
      fileKey: 0,
      revision: 0,
      uploadLabel: "",
      uploadSession: null,
      uploadProgress: 0,
      saving: false,
      saveUncertain: false,
      downloadingId: "",
      dialogError: "",
      snackbar: { show: false, color: "success", text: "" },
    }
  },
  computed: {
    isLeadership() {
      return this.area === "leadership"
    },
    versionGroups() {
      if (!this.versionDocument) return []
      return this.isLeadership ? this.documentFiles(this.versionDocument) : [this.versionDocument]
    },
    areaLabel() {
      return this.area === "leadership" ? "Leadership Docs" : "Guidelines"
    },
    categoryOptions() {
      const categories = ["Allgemein", ...this.documents.map((document) => document.category)]
      return [...new Set(categories.map((category) => String(category || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "de-CH"))
    },
    fileAccept() {
      return getAcademyDocumentAccept(this.area)
    },
    fileFormatsHint() {
      return this.area === "leadership" ? "PDF, Word (.doc, .docx) · maximal 50 MB" : "PDF · maximal 50 MB"
    },
    fileRule() {
      return (file) => {
        if (!file) return this.editingId ? true : "Datei erforderlich"
        const type = getAcademyDocumentType(file)
        if (type !== "pdf" && !(this.area === "leadership" && ["doc", "docx"].includes(type))) {
          return this.area === "leadership" ? "Bitte eine PDF- oder Word-Datei (.doc, .docx) wählen" : "Bitte eine PDF-Datei wählen"
        }
        if (!file.size) return "Die Datei ist leer"
        return file.size <= MAX_FILE_SIZE || "Datei maximal 50 MB"
      }
    },
  },
  mounted() {
    this.loadDocuments()
  },
  watch: {
    area() {
      this.loadDocuments()
    },
    file() {
      const session = this.uploadSession
      this.uploadSession = null
      this.uploadProgress = 0
      if (session && this.editingId) api.akademie.cancelAcademyDocumentUpload(this.editingId, session).catch(() => {})
    },
  },
  methods: {
    documentIcon: getAcademyDocumentIcon,
    documentFiles(document) {
      if (Array.isArray(document.files)) return document.files
      return document.currentFile ? [{ _id: document._id, currentFile: document.currentFile,
        version: document.version, versions: document.versions || [], deletedAt: null }] : []
    },
    activeFiles(document) {
      return this.documentFiles(document).filter((entry) => !entry.deletedAt)
    },
    versionCount(document) {
      return this.isLeadership ? this.documentFiles(document).reduce((count, entry) => count + (entry.versions || []).length, 0)
        : (document.versions || []).length
    },
    async discardUpload(entry) {
      const session = entry.session
      entry.session = null
      if (session && this.editingId) {
        await api.akademie.cancelAcademyDocumentUpload(this.editingId, session).catch(() => {})
      }
    },
    addFiles(files) {
      if (this.saving || !files || !files.length) return
      for (const file of files) {
        const rule = this.fileRule(file)
        this.fileEntries.push({ key: ++this.fileKey, _id: null, file, currentFile: null,
          session: null, removed: false, error: rule === true ? "" : rule })
      }
      this.$nextTick(() => { this.selectedFiles = [] })
    },
    setReplacement(entry, file) {
      if (this.saving) return
      this.discardUpload(entry)
      entry.file = file || null
      const rule = file ? this.fileRule(file) : true
      entry.error = rule === true ? "" : rule
    },
    toggleFile(entry) {
      if (this.saving) return
      this.discardUpload(entry)
      if (!entry._id) this.fileEntries = this.fileEntries.filter((item) => item !== entry)
      else entry.removed = !entry.removed
    },
    emptyForm() {
      return { title: "", category: "Allgemein", description: "", active: true }
    },
    notify(color, text) {
      this.snackbar = { show: true, color, text }
    },
    async loadDocuments() {
      this.loading = true
      try {
        this.documents = (await api.akademie.fetchAcademyDocuments(this.area, true)) || []
      } catch (error) {
        this.notify("error", "Die Dokumente konnten nicht geladen werden.")
      } finally {
        this.loading = false
      }
    },
    openDialog(document = null) {
      this.editingId = document && document._id
      this.form = document
        ? { title: document.title || "", category: document.category || "Allgemein", description: document.description || "", active: !!document.active }
        : this.emptyForm()
      this.file = null
      this.selectedFiles = []
      this.fileEntries = document && this.isLeadership ? this.activeFiles(document).map((entry) => ({
        ...entry, key: ++this.fileKey, file: null, session: null, removed: false, error: "",
      })) : []
      this.revision = (document && document.revision) || 0
      this.uploadLabel = ""
      this.saveUncertain = false
      this.uploadSession = null
      this.uploadProgress = 0
      this.dialogError = ""
      this.dialog = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation()
      })
    },
    closeDialog() {
      if (this.saving) return
      const session = this.uploadSession
      const documentId = this.editingId
      this.uploadSession = null
      this.dialog = false
      this.fileEntries.forEach((entry) => this.discardUpload(entry))
      this.loadDocuments()
      if (session && documentId) api.akademie.cancelAcademyDocumentUpload(documentId, session).catch(() => {})
    },
    async saveDocument() {
      if (this.saving || this.saveUncertain) return
      // Freie Eingaben vor dem Speichern in das Combobox-Modell übernehmen.
      if (this.$refs.categoryInput) this.$refs.categoryInput.blur()
      await this.$nextTick()
      if (!this.$refs.form || !this.$refs.form.validate() || this.saving) return
      if (this.isLeadership) {
        const remaining = this.fileEntries.filter((entry) => !entry.removed)
        if (!remaining.length && (!this.editingId || this.form.active)) {
          this.dialogError = "Bitte füge mindestens eine Datei hinzu oder speichere den bestehenden Eintrag als Entwurf."
          return
        }
        if (remaining.some((entry) => entry.file && this.fileRule(entry.file) !== true)) {
          this.dialogError = "Bitte korrigiere die markierten Dateien."
          return
        }
      }
      this.saving = true
      this.dialogError = ""
      this.uploadProgress = 0
      this.uploadLabel = ""
      let documentId = this.editingId
      let committing = false
      try {
        if (!documentId) {
          const created = await api.akademie.createAcademyDocument({ area: this.area, ...this.form, active: false })
          documentId = created._id
          this.editingId = documentId
          this.revision = created.revision || 0
        }
        const payload = { area: this.area, ...this.form }
        if (this.isLeadership) {
          payload.revision = this.revision
          payload.fileChanges = this.fileEntries.filter((entry) => entry._id && entry.removed).map((entry) => ({ id: entry._id, remove: true }))
          const pending = this.fileEntries.filter((entry) => !entry.removed && entry.file)
          for (const [index, entry] of pending.entries()) {
            this.uploadLabel = `Datei ${index + 1} von ${pending.length}: ${entry.file.name}`
            entry.session = await api.akademie.prepareAcademyDocumentUpload(documentId, entry.file)
            await uploadCloudflareMedia(entry.file, entry.session, {
              onProgress: (loaded, total) => {
                this.uploadProgress = Math.round(((index + loaded / Math.max(1, total || entry.file.size)) * 100) / pending.length)
              },
            })
            payload.fileChanges.push({ ...(entry._id ? { id: entry._id } : {}), media: entry.session })
          }
        } else if (this.file) {
          this.uploadSession = await api.akademie.prepareAcademyDocumentUpload(documentId, this.file)
          await uploadCloudflareMedia(this.file, this.uploadSession, {
            onProgress: (loaded, total) => {
              this.uploadProgress = Math.round((loaded * 100) / Math.max(1, total || this.file.size))
            },
          })
          payload.media = this.uploadSession
        }
        committing = true
        await api.akademie.updateAcademyDocument(documentId, payload)
        this.uploadSession = null
        this.fileEntries.forEach((entry) => { entry.session = null })
        this.dialog = false
        await this.loadDocuments()
        this.notify("success", "Dokument und Versionsverlauf wurden gespeichert.")
      } catch (error) {
        if (this.isLeadership && committing && !error.response) {
          // The server may still commit after a lost response. Never delete those uploads.
          this.fileEntries.forEach((entry) => { entry.session = null })
          this.saveUncertain = true
          this.dialogError = "Die Speicherbestätigung fehlt. Bitte schliesse den Dialog und prüfe den neu geladenen Eintrag, bevor du weitere Änderungen speicherst."
          return
        }
        await Promise.all(this.fileEntries.map((entry) => this.discardUpload(entry)))
        this.dialogError =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Das Dokument konnte nicht gespeichert werden."
      } finally {
        this.saving = false
      }
    },
    async deleteDocument(document) {
      if (!confirm(`Dokument «${document.title}» wirklich ausblenden und löschen? Der Dateiverlauf bleibt zur Nachvollziehbarkeit gespeichert.`)) return
      try {
        await api.akademie.deleteAcademyDocument(document._id)
        await this.loadDocuments()
        this.notify("success", "Dokument gelöscht.")
      } catch (error) {
        this.notify("error", "Das Dokument konnte nicht gelöscht werden.")
      }
    },
    openVersions(document) {
      this.versionDocument = document
      this.versionsDialog = true
    },
    async downloadVersion(document, version, group) {
      this.downloadingId = version._id
      try {
        const filename = getAcademyDocumentFileName(version.file, `${document.title}-v${version.version}`)
        if (this.isLeadership) {
          await api.akademie.downloadAcademyAttachmentVersion(document._id, group._id, version._id, filename)
        } else {
          await api.akademie.downloadAcademyDocumentVersion(document._id, version._id, filename)
        }
      } catch (error) {
        this.notify("error", "Die Dokumentversion konnte nicht geöffnet werden.")
      } finally {
        this.downloadingId = ""
      }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString("de-CH", { dateStyle: "medium", timeStyle: "short" }) : "-"
    },
  },
}
</script>

<style scoped>
.attachment-editor, .version-group {
  display: grid;
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.attachment-editor h3, .version-group h3 {
  margin: 0;
  color: #fff;
  font-size: 1rem;
  overflow-wrap: anywhere;
}
.attachment-editor p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
}
.attachment-row {
  min-width: 0;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.875rem;
}
.attachment-row.removed {
  border-style: dashed;
}
.attachment-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.625rem;
  margin-bottom: 0.5rem;
}
.attachment-heading strong, .attachment-heading small {
  display: block;
  overflow-wrap: anywhere;
}
.attachment-heading strong { color: #fff; font-size: 1rem; }
.attachment-heading small { color: #a3a3a8; font-size: 0.75rem; }
.attachment-editor .attachment-error { margin-top: 0.5rem; color: #ff8d98; }
.upload-progress > div { gap: 0.75rem; overflow-wrap: anywhere; }
.upload-progress > div > span { min-width: 0; }

.document-manager {
  color: #f4f4f5;
  font-size: 1rem;
}

.manager-header,
.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.eyebrow,
.document-heading small {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.manager-header h2,
.dialog-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.manager-header p,
.dialog-header p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.create-action,
.dialog-actions .v-btn {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.document-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.document-card {
  display: flex;
  min-height: 14rem;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.document-card.inactive {
  border-style: dashed;
  opacity: 0.75;
}

.document-heading {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.625rem;
}

.document-heading > span:first-child,
.dialog-header > span,
.version-row > span {
  display: grid;
  place-items: center;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.document-heading > span:first-child {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.8125rem;
}

.document-heading .v-icon,
.dialog-header > span .v-icon,
.version-row > span .v-icon {
  color: currentColor;
}

.document-heading h3 {
  margin: 0.125rem 0 0;
  color: #fff;
  font-size: 1rem;
  line-height: 1.35;
}

.status-badge {
  padding: 0.375rem 0.625rem;
  border-radius: 999px;
  color: #c7c7cb;
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.75rem;
  font-weight: 750;
}

.status-badge.active {
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
}

.document-card > p {
  flex: 1 1 auto;
  margin: 0.875rem 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: break-spaces;
  overflow-wrap: anywhere;
}

.document-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  color: #8f9095;
  font-size: 0.75rem;
}

.document-meta span {
  display: inline-flex;
  align-items: center;
  gap: 0.3125rem;
}

.document-meta .v-icon {
  color: #8f9095;
  font-size: 1rem;
}

.document-card footer {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.75rem;
  padding-top: 0.625rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.document-card footer .v-btn {
  font-size: 0.875rem;
  text-transform: none;
}

.document-card footer .v-btn:last-child {
  margin-left: auto;
}

.empty-state {
  display: grid;
  min-height: 16rem;
  place-items: center;
  align-content: center;
  margin-top: 1rem;
  text-align: center;
}

.empty-state .v-icon {
  color: #7ee5a6;
  font-size: 2.75rem;
}

.empty-state h3 {
  margin: 0.75rem 0 0.25rem;
  color: #fff;
  font-size: 1.125rem;
}

.empty-state p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
}

.dialog-card {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.dialog-header {
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.dialog-header > span {
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  border-radius: 0.8125rem;
}

.dialog-header > div {
  min-width: 0;
  flex: 1 1 auto;
}

.dialog-body {
  padding: 1.25rem !important;
}

.publish-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.14);
}

.publish-control.active {
  border-color: rgba(0, 150, 64, 0.34);
  background: rgba(0, 150, 64, 0.07);
}

.publish-control > div {
  display: grid;
  gap: 0.125rem;
}

.publish-control strong {
  color: #fff;
  font-size: 0.875rem;
}

.publish-control span {
  color: #a3a3a8;
  font-size: 0.75rem;
}

.upload-progress {
  margin-top: 1rem;
}

.upload-progress > div {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: #b8b8bc;
  font-size: 0.875rem;
}

.dialog-actions {
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.version-list {
  display: grid;
  gap: 0.5rem;
  padding: 1rem 1.25rem !important;
}

.version-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.8125rem;
}

.version-row > span {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
}

.version-row > div {
  display: grid;
  min-width: 0;
  gap: 0.125rem;
}

.version-row strong {
  color: #fff;
  font-size: 0.875rem;
}

.version-row small {
  overflow: hidden;
  color: #8f9095;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 72rem) {
  .document-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 43rem) {
  .manager-header {
    align-items: stretch;
    flex-direction: column;
  }

  .document-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .document-heading {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .status-badge {
    grid-column: 2;
    justify-self: start;
  }
}
</style>
